import { useState, memo, useEffect } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { Package, Trash2, CirclePlus, Logs, LayoutGrid } from "lucide-mui";
import { TocSectionHeader } from "./TocSectionHeader";
import { useTheme } from "../context/ThemeContext";
import { PartCard } from "../pages/partsViewer3D";
import {
  EditSectionRoot,
  EditTableCard,
  EditTableHeaderRow,
  EditTableHeaderLabel,
  EditTableDataRows,
  EditTableRow,
  AddEntityButtonWrap,
  AddEntityButton,
  AddEntityButtonLabel,
  TogglePill,
  ToggleOption,
} from "./ui";
import { resolvePartsView, getPart3DMeta } from "../utils/partsView";

const COL = "20px 2fr 1fr 40px";

function AddPartIcon({ isDark }) {
  const color = isDark ? "#ECEFF3" : "#434950";
  return (
    <Box
      component='svg'
      xmlns='http://www.w3.org/2000/svg'
      width={16}
      height={16}
      viewBox='0 0 16 16'
      fill='none'
      aria-hidden
      sx={{ flexShrink: 0, display: "block" }}
    >
      <circle cx='8' cy='8' r='6.25' stroke={color} strokeWidth='1.4' />
      <path
        d='M8 5.4v5.2M5.4 8h5.2'
        stroke={color}
        strokeWidth='1.4'
        strokeLinecap='round'
      />
    </Box>
  );
}

function CellInput({ value, onChange, onBlur, placeholder, type, isDark, isError }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        background: isDark ? "#FFFFFF0A" : "#FFFFFF",
        border: "1px solid",
        borderColor: isError ? "#C12E30" : isDark ? "#555555" : "#D5D9DF",
        borderRadius: "10px",
        px: "8px",
        py: "6px",
        height: "30px",
        width: type === "number" ? "48px" : undefined,
        minWidth: type === "number" ? "48px" : "60px",
      }}
    >
      <Box
        component='input'
        autoFocus
        value={value ?? ""}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        type={type}
        sx={{
          width: "100%",
          border: "none",
          outline: "none",
          background: "transparent",
          fontFamily: "Inter",
          fontWeight: 400,
          fontSize: "14px",
          lineHeight: "20px",
          letterSpacing: "0.035px",
          color: isDark ? "#ECEFF3" : "#434950",
          MozAppearance: "textfield",
          appearance: "textfield",
          "&::-webkit-inner-spin-button, &::-webkit-outer-spin-button": {
            WebkitAppearance: "none",
            appearance: "none",
            margin: 0,
          },
          "&::placeholder": { color: isDark ? "#ECEFF333" : "#43495033" },
        }}
      />
    </Box>
  );
}

function PartRow({
  part,
  index,
  activeCell,
  hoveredCell,
  onActivate,
  onBlur,
  onCellEdit,
  onDelete,
  onCellHover,
  onCellHoverEnd,
  isDark,
  canEdit,
  showErrors,
}) {
  const cellId = field => `${index}.${field}`;

  const renderCell = (field, displayText, placeholder, type) => {
    const id = cellId(field);
    const isEditing = activeCell === id;
    const isHovered = canEdit && hoveredCell === id;
    const isEmpty = displayText == null || displayText === "";
    const isError = showErrors && (
      field === "name" ? (!part.name || part.name === "") :
      field === "quantity" ? (part.quantity == null || part.quantity === "") :
      false
    );

    if (isEditing) {
      return (
        <CellInput
          value={part[field] ?? ""}
          onChange={e => onCellEdit(index, field, e.target.value)}
          onBlur={onBlur}
          placeholder={placeholder}
          type={type}
          isDark={isDark}
          isError={isError}
        />
      );
    }

    return (
      <Box
        onClick={canEdit ? () => onActivate(id) : undefined}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          height: "30px",
          maxWidth: "100%",
          borderRadius: "10px",
          px: "8px",
          cursor: canEdit ? "text" : "default",
          border: "1px solid",
          borderColor: isError ? "#C12E30" : "transparent",
          background: isHovered
            ? isDark ? "#3A3A3BB3" : "#F3F6FA"
            : "transparent",
          transition: "background 0.12s",
        }}
      >
        <Typography
          sx={{
            fontFamily: "Inter",
            fontWeight: 400,
            fontSize: "14px",
            lineHeight: "20px",
            letterSpacing: "0.035px",
            color: isEmpty
              ? isDark
                ? "#ECEFF333"
                : "#43495033"
              : isDark
                ? "#ECEFF3"
                : "#434950",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {isEmpty ? "—" : displayText}
        </Typography>
      </Box>
    );
  };

  const qtyDisplay =
    part.quantity != null && part.quantity !== "" ? `${part.quantity} x` : null;

  return (
    <EditTableRow isDark={isDark} columns={COL}>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Package
          sx={{ fontSize: "16px", color: isDark ? "#A8AEB6" : "#9299A1" }}
        />
      </Box>

      {/* Part Name */}
      <Box
        sx={{ display: "flex", alignItems: "center", minWidth: 0 }}
        onMouseEnter={canEdit ? () => onCellHover(cellId("name")) : undefined}
        onMouseLeave={canEdit ? onCellHoverEnd : undefined}
      >
        {renderCell("name", part.name, "Part name...", "text")}
      </Box>

      {/* Quantity */}
      <Box
        sx={{ display: "flex", alignItems: "center", minWidth: 0 }}
        onMouseEnter={
          canEdit ? () => onCellHover(cellId("quantity")) : undefined
        }
        onMouseLeave={canEdit ? onCellHoverEnd : undefined}
      >
        {renderCell("quantity", qtyDisplay, "1", "number")}
      </Box>

      {/* Delete */}
      <Box
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        {canEdit && (
          <IconButton
            onClick={e => {
              e.stopPropagation();
              onDelete(index);
            }}
            size='small'
            sx={{
              p: "4px",
              color: isDark ? "#6A7079" : "#A8AEB6",
              "&:hover": { backgroundColor: "transparent" },
            }}
          >
            <Trash2 sx={{ fontSize: "15px" }} />
          </IconButton>
        )}
      </Box>
    </EditTableRow>
  );
}

const PartsEditSectionComponentInner = ({
  data,
  subtitle,
  onEdit,
  onToggleSidebar,
  partsMetadata,
  modelRef,
  modelLoaded,
  showErrors = false,
}) => {
  const { isDark } = useTheme();
  const [activeCell, setActiveCell] = useState(null);
  const [hoveredCell, setHoveredCell] = useState(null);
  const [viewOverride, setViewOverride] = useState(null);
  const [autoView, setAutoView] = useState(null);

  const canEdit = !!onEdit;
  const items = data?.items ?? [];
  const modelReady = partsMetadata != null && modelRef != null && modelLoaded;

  useEffect(() => {
    if (autoView != null || !modelReady) return;
    const currentItems = data?.items ?? [];
    if (currentItems.length === 0) return;
    setAutoView(
      resolvePartsView({
        items: currentItems,
        partsMetadata,
        modelReady: true,
      }),
    );
  }, [autoView, modelReady, partsMetadata, data?.items]);

  const view = viewOverride ?? autoView ?? "list";

  const viewToggle = (
    <TogglePill isDark={isDark}>
      <ToggleOption
        isDark={isDark}
        active={view === "list"}
        onClick={() => view !== "list" && setViewOverride("list")}
      >
        <Logs />
      </ToggleOption>
      <ToggleOption
        isDark={isDark}
        active={view === "grid"}
        onClick={() => view !== "grid" && setViewOverride("grid")}
      >
        <LayoutGrid />
      </ToggleOption>
    </TogglePill>
  );

  const sectionHeader = (
    <TocSectionHeader
      icon={
        <Package
          sx={{ fontSize: "24px", color: isDark ? "#41D179" : "#146E37" }}
        />
      }
      title='Parts and Assembly'
      subtitle={subtitle}
      onToggleSidebar={onToggleSidebar}
      actions={viewToggle}
    />
  );
  const handleCellEdit = (index, field, value) => {
    const newItems = items.map((item, i) =>
      i === index
        ? { ...item, [field]: field === "quantity" && value !== "" ? parseInt(value) || 0 : value }
        : item,
    );
    onEdit("items", newItems);
  };

  const handleDelete = index => {
    const newItems = items.filter((_, i) => i !== index);
    onEdit("items", newItems);
    setHoveredCell(null);
  };

  const handleAddPart = () => {
    const newItems = [...items, { name: "", quantity: 1 }];
    onEdit("items", newItems);
    setTimeout(() => setActiveCell(`${newItems.length - 1}.name`), 0);
  };

  if (view === "grid") {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {sectionHeader}
        <Box
          sx={{
            mt: 2,
            flex: 1,
            overflowY: "auto",
            overflowX: "auto",
            minWidth: 0,
            "&::-webkit-scrollbar": { width: "4px" },
            "&::-webkit-scrollbar-button": { display: "none", height: 0 },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              background: "#6A7079",
              borderRadius: "6px",
            },
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fill, minmax(max(160px, min(100%/5, 100%/2)), 1fr))",
              gap: 3,
              minWidth: 0,
            }}
          >
            {items.map((part, index) => {
              const meta = getPart3DMeta(part, partsMetadata);
              const partFor3D = meta?.full_path
                ? { full_path: meta.full_path }
                : null;
              return (
                <Box key={index} sx={{ minWidth: 0 }}>
                  <PartCard
                      item={{ name: part.name, quantity: part.quantity ?? 1 }}
                      partFor3D={partFor3D}
                      modelRef={modelRef}
                      modelLoaded={modelLoaded}
                      zoomToFill
                      showErrors={showErrors}
                      onDelete={
                        canEdit ? () => handleDelete(index) : undefined
                      }
                      onQuantityChange={
                        canEdit
                          ? value => {
                              const newItems = [...items];
                              newItems[index] = {
                                ...newItems[index],
                                quantity: value,
                              };
                              onEdit("items", newItems);
                            }
                          : undefined
                      }
                      onNameChange={
                        canEdit
                          ? value => {
                              const newItems = [...items];
                              newItems[index] = {
                                ...newItems[index],
                                name: value,
                              };
                              onEdit("items", newItems);
                            }
                          : undefined
                      }
                    />
                </Box>
              );
            })}
            {canEdit && (
              <Box sx={{ minWidth: 0 }}>
                <Box
                  component='button'
                  type='button'
                  aria-label='Add part'
                  title='Add Part'
                  onClick={handleAddPart}
                  sx={{
                    width: "100%",
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "12px",
                    border: "none",
                    bgcolor: isDark ? "#262A2E" : "#FFFFFF",
                    cursor: "pointer",
                    boxSizing: "border-box",
                    p: 0,
                    m: 0,
                    font: "inherit",
                    overflow: "hidden",
                    "&:hover": {
                      bgcolor: isDark ? "#2a2d30" : "#F3F6FA",
                      "& .add-part-dash rect": {
                        stroke: isDark ? "#434950" : "rgba(0,0,0,0.35)",
                      },
                    },
                  }}
                >
                  <Box
                    component='svg'
                    className='add-part-dash'
                    xmlns='http://www.w3.org/2000/svg'
                    aria-hidden
                    sx={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      pointerEvents: "none",
                      "& rect": {
                        x: 2,
                        y: 2,
                        width: "calc(100% - 4px)",
                        height: "calc(100% - 4px)",
                        rx: 12,
                        ry: 12,
                        fill: "none",
                        stroke: isDark ? "#31363A" : "rgba(0,0,0,0.28)",
                        strokeWidth: 4,
                        strokeDasharray: "6 8",
                        strokeLinecap: "round",
                      },
                    }}
                  >
                    <rect />
                  </Box>
                  <Box
                    sx={{
                      aspectRatio: "280/189",
                      flexShrink: 0,
                      minHeight: 120,
                    }}
                  />
                  <Box sx={{ minHeight: "41px", flexShrink: 0 }} />
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      zIndex: 1,
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                    }}
                  >
                    <AddPartIcon isDark={isDark} />
                    <Typography
                      component='span'
                      sx={{
                        fontFamily: "Inter",
                        fontWeight: 500,
                        fontSize: "14px",
                        lineHeight: "16px",
                        height: "16px",
                        display: "inline-flex",
                        alignItems: "center",
                        color: isDark ? "#ECEFF3" : "#434950",
                      }}
                    >
                      Add Part
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <EditSectionRoot>
      {sectionHeader}

      {/* Table */}
      <EditTableCard isDark={isDark}>
        {/* Header row */}
        <EditTableHeaderRow isDark={isDark} columns={COL}>
          <Box />
          {["Part Name", "Quantity"].map(h => (
            <EditTableHeaderLabel key={h} isDark={isDark}>
              {h}
            </EditTableHeaderLabel>
          ))}
          <Box />
        </EditTableHeaderRow>

        {/* Data rows */}
        <EditTableDataRows>
          {items.map((part, index) => (
            <PartRow
              key={index}
              part={part}
              index={index}
              activeCell={activeCell}
              onActivate={setActiveCell}
              onBlur={() => setActiveCell(null)}
              onCellEdit={handleCellEdit}
              onDelete={handleDelete}
              hoveredCell={hoveredCell}
              onCellHover={setHoveredCell}
              onCellHoverEnd={() => setHoveredCell(null)}
              isDark={isDark}
              canEdit={canEdit}
              showErrors={showErrors}
            />
          ))}
        </EditTableDataRows>
      </EditTableCard>

      {/* Add Part button — outside table, only for editors */}
      {canEdit && (
        <AddEntityButtonWrap>
          <AddEntityButton isDark={isDark} onClick={handleAddPart}>
            <CirclePlus
              sx={{ fontSize: "16px", color: isDark ? "#ECEFF3" : "#434950" }}
            />
            <AddEntityButtonLabel isDark={isDark}>
              Add Part
            </AddEntityButtonLabel>
          </AddEntityButton>
        </AddEntityButtonWrap>
      )}
    </EditSectionRoot>
  );
};

export const PartsEditSectionComponent = memo(PartsEditSectionComponentInner);
